import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Cpu, Activity, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

function AssetCard({ a }) {
  const isLuchii = a.family === "Luchii";
  return (
    <div
      data-testid={`asset-card-${a.name.toLowerCase().replace(/\s/g, "-")}`}
      className="group rounded-2xl border border-emerald-900/60 surface-panel p-6 hover-emerald-glow flex flex-col"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${isLuchii ? "border-emerald-400/40 bg-emerald-500/10" : "border-gold/40 bg-gold/5"}`}>
            <Cpu className={`w-5 h-5 ${isLuchii ? "text-emerald-300" : "text-gold"}`} />
          </div>
          <div>
            <div className="text-emerald-50 font-semibold font-garamond text-lg tracking-wide">{a.name}</div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">{a.tier} · {a.version}</div>
          </div>
        </div>
        <Badge className={`${a.status === "Live" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-gold/15 text-gold border-gold/30"} border rounded-full text-[10px] tracking-wider`}>
          {a.status}
        </Badge>
      </div>

      <p className="text-sm text-slate-400 leading-relaxed flex-1">{a.description}</p>

      <div className="grid grid-cols-3 gap-3 mt-5 mb-4">
        <div><div className="font-mono text-emerald-200 text-sm">{a.params}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">Params</div></div>
        <div><div className="font-mono text-emerald-200 text-sm">{a.context_window}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">Context</div></div>
        <div><div className="font-mono text-emerald-200 text-sm">{a.accuracy}%</div><div className="text-[10px] uppercase tracking-wider text-slate-500">MMLU</div></div>
      </div>

      <div>
        <div className="flex justify-between text-[11px] text-slate-500 mb-1.5">
          <span className="flex items-center gap-1"><Activity className="w-3 h-3" /> Compute Load</span>
          <span className="text-emerald-300">{a.compute_load}%</span>
        </div>
        <Progress value={a.compute_load} className="h-1.5 surface-input" />
        <div className="flex items-center gap-1 mt-3 text-[11px] text-slate-500">
          <Zap className="w-3 h-3 text-gold" /> {a.inference_rps.toLocaleString()} req/s
        </div>
      </div>
    </div>
  );
}

export default function Portfolio() {
  const [assets, setAssets] = useState([]);

  useEffect(() => {
    api.get("/assets").then(({ data }) => setAssets(data)).catch(() => {});
  }, []);

  const luchii = assets.filter((a) => a.family === "Luchii");
  const frasberg = assets.filter((a) => a.family === "Frasberg");

  return (
    <section id="portfolio" className="py-20 lg:py-28 royal-radial">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <p className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-semibold mb-3">Asset Portfolio</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-emerald-50 uppercase tracking-tight">
            Two Families. <span className="gold-gradient-text">One Continuum.</span>
          </h2>
          <p className="mt-5 text-slate-300 font-light">
            Our holdings span the full spectrum of intelligence — from lightning draft models to
            frontier-class enterprise cognition.
          </p>
        </div>

        <div className="mb-6 flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          <h3 className="font-garamond text-xl text-emerald-100 tracking-wide">Luchii AI Models — Generative Core</h3>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {luchii.map((a) => <AssetCard key={a.id} a={a} />)}
        </div>

        <div className="mb-6 flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-gold" />
          <h3 className="font-garamond text-xl text-emerald-100 tracking-wide">Frasberg AI Models — Enterprise Core</h3>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {frasberg.map((a) => <AssetCard key={a.id} a={a} />)}
        </div>
      </div>
    </section>
  );
}
